import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { CREATEUSER, GETALLUSERS } from "app/redux/actions/users";
import { USER_REQUEST } from "app/redux/actions/utilities";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, SimpleCard } from "app/components";
import { TextField1 } from "app/assets";
import { Formik } from "formik";
import * as Yup from "yup";
import { PATH, LIVE_URL } from "../../../config";

import Loading from "app/components/MatxLoading";
import { isImageFile } from "app/assets/genericActions";

import { toast } from "react-toastify";

const CreateEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [newEmployeeCredentials, setNewEmployeeCredentials] = useState(null);

  const loggedInUser = useSelector((state) => state.user.user);

  const user = useSelector((state) => state.user);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    cnic: Yup.string()
      .matches(
        /(^\d{13}$)|(^\d{5}-\d{7}-\d{1}$)/,
        "Invalid CNIC. Use: 0000000000000 or 00000-0000000-0"
      )
      .required("CNIC is required"),
    phone: Yup.string()
      .matches(
        /(^0\d{10}$)|(^\d{4}-\d{7}$)|(^\+92\d{10}$)|(^\+92 \d{10}$)/,
        "Invalid phone. Use: 03xxxxxxxxx, 03xx-xxxxxxx, +923xxxxxxxxx, or +92 3xxxxxxxxx"
      )
      .required("Mobile is required"),
    account_no: Yup.string().required("Account Number is required"),
    joiningDate: Yup.date().required("Joining date is required").nullable(),
    employmentStatus: Yup.string().required("Employment status is required"),
    status: Yup.string().required("Status is required"),
    isTeamLead: Yup.boolean(),
    teamLeadId: Yup.string().notRequired().nullable(),
    reportTo: Yup.string().notRequired(),
    deviceId: Yup.string().required("Device ID is required"),
  });

  useEffect(() => {
    dispatch({ type: USER_REQUEST.RESET_USER_DATA });

  }, [dispatch]);

  useEffect(() => {
    if (loggedInUser?.token) {
      dispatch(
        GETALLUSERS(
          { page: 1, limit: 1000, userType: "admin,teamlead" },
          loggedInUser
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loggedInUser?.token]);

  const initialValues = {
    name: "",
    email: "",
    cnic: "",
    phone: "",
    account_no: "",
    joiningDate: "",
    teamLeadId: null,
    status: "active",
    isTeamLead: false,
    userType: "employee",
    avatar: "",
    cnic_front: null,
    cnic_back: null,
    employmentStatus: "Probation",
    reportTo: "",
    deviceId: "",
  };

  const handleFileUpload = (event, fieldName, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;
    setFieldValue(fieldName, file); // Store File object
    toast.success(`${fieldName.replace("_", " ")} selected successfully!`);
  };
  const handleRemoveFile = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };
  const handleSubmit = (values) => {
    const formData = new FormData();

    for (const key in values) {
      if (values[key] instanceof File) {
        formData.append(key, values[key]); // file
      } else {
        formData.append(key, values[key] ?? ""); // other fields
      }
    }

    formData.append("userType", values.isTeamLead ? "teamlead" : "employee");

    const onSaveSuccess = (credentials) => {
      toast.success("Employee created successfully!");
      setNewEmployeeCredentials(credentials);
      setShowCredentialsDialog(true);
    };
    const onfailure = (message) => {
      toast.error(message);
    };
    dispatch(
      CREATEUSER(
        formData,
        loggedInUser?.token || null,
        onSaveSuccess,
        onfailure
      )
    );
  };

  const handleCloseCredentialsDialog = () => {
    setShowCredentialsDialog(false);
    navigate(PATH.ALLEMPLOYEES);
  };

  if (user.userLoading || !initialValues) {
    return <Loading />;
  }

  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Create Employee" }]} />
      </Box>
      <Formik
        initialValues={initialValues || {}}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          errors,
          touched,
        }) => {
          return (
            <Box component="form" onSubmit={handleSubmit} sx={{ padding: 2 }}>
              <Grid container spacing={3}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="name"
                    label="Name *"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="email"
                    label="Email *"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="cnic"
                    label="CNIC Number *"
                    value={values.cnic}
                    onChange={handleChange}
                    error={touched.cnic && Boolean(errors.cnic)}
                    helperText={touched.cnic && errors.cnic}
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="phone"
                    label="Mobile Number *"
                    value={values.phone}
                    onChange={handleChange}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Grid>
                
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <label style={{ marginBottom: "5px", display: "block", color: "#666" }}>
                    Account Number *
                  </label>
                  <textarea
                    name="account_no"
                    rows="4"
                    value={values.account_no}
                    onChange={handleChange}
                    placeholder="Enter Account Number details..."
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "4px",
                      border: "1px solid #c4c4c4", 
                      fontFamily: "inherit",
                      fontSize: "16px",
                      resize: "vertical"
                    }}
                  />
                  {touched.account_no && errors.account_no && (
                    <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginLeft: "14px", marginTop: "3px" }}>
                      {errors.account_no}
                    </div>
                  )}
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="joiningDate"
                    label="Joining Date *"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={values.joiningDate}
                    onChange={handleChange}
                    error={touched.joiningDate && Boolean(errors.joiningDate)}
                    helperText={touched.joiningDate && errors.joiningDate}
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    select
                    fullWidth
                    name="employmentStatus"
                    label="Employment Status *"
                    value={values.employmentStatus}
                    onChange={handleChange}
                    error={
                      touched.employmentStatus &&
                      Boolean(errors.employmentStatus)
                    }
                    helperText={
                      touched.employmentStatus && errors.employmentStatus
                    }
                  >
                    <MenuItem value="Probation">Probation</MenuItem>
                    <MenuItem value="Contractual">Contractual</MenuItem>
                    <MenuItem value="Internship">Internship</MenuItem>
                  </TextField>
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="status"
                    label="Status"
                    select
                    SelectProps={{ native: true }}
                    value={values.status}
                    onChange={handleChange}
                    error={touched.status && Boolean(errors.status)}
                    helperText={touched.status && errors.status}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </TextField1>
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    select
                    fullWidth
                    name="reportTo"
                    label="Assign Report To"
                    value={values.reportTo}
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 250 } } },
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(user?.allUsers?.users || []).map((lead) => (
                      <MenuItem key={lead.id} value={lead.id}>
                        {lead.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField1
                    fullWidth
                    name="deviceId"
                    label="Device ID *"
                    value={values.deviceId}
                    onChange={handleChange}
                    error={touched.deviceId && Boolean(errors.deviceId)}
                    helperText={touched.deviceId && errors.deviceId}
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isTeamLead"
                        checked={values.isTeamLead}
                        onChange={handleChange}
                      />
                    }
                    label="Is Team Lead"
                  />
                </Grid>

                {!values.isTeamLead ? (
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="teamLeadId"
                      label="Assign Team Lead (Optional)"
                      value={values.teamLeadId}
                      onChange={handleChange}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 250 } },
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(user?.allUsers?.users || [])
                        .filter((user) => user.userType !== "admin")
                        .map((lead) => (
                          <MenuItem key={lead.id} value={lead.id}>
                            {lead.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                ) : (
                  <Grid item lg={6} md={6} sm={12} xs={12} />
                )}

                <Grid item lg={6} md={6} sm={12} xs={12} />

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                  >
                    Upload CNIC Front
                    <input
                      type="file"
                      hidden
                      onChange={(event) =>
                        handleFileUpload(event, "cnic_front", setFieldValue)
                      }
                    />
                  </Button>

                  {/* Show selected file with remove */}
                  {values.cnic_front && (
                    <Box mt={1} display="flex" alignItems="center" gap={2}>
                      {isImageFile(values.cnic_front) ? (
                        <img
                          src={
                            typeof values.cnic_front === "string"
                              ? LIVE_URL + values.cnic_front
                              : URL.createObjectURL(values.cnic_front)
                          }
                          alt="CNIC Front"
                          style={{
                            width: 100,
                            height: "auto",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <AttachFileIcon fontSize="large" />
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          handleRemoveFile("cnic_front", setFieldValue)
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                  >
                    Upload CNIC Back
                    <input
                      type="file"
                      hidden
                      onChange={(event) =>
                        handleFileUpload(event, "cnic_back", setFieldValue)
                      }
                    />
                  </Button>
                  {values.cnic_back && (
                    <Box mt={1} display="flex" alignItems="center" gap={2}>
                      {isImageFile(values.cnic_back) ? (
                        <img
                          src={
                            typeof values.cnic_back === "string"
                              ? LIVE_URL + values.cnic_back
                              : URL.createObjectURL(values.cnic_back)
                          }
                          alt="CNIC Front"
                          style={{
                            width: 100,
                            height: "auto",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <AttachFileIcon fontSize="large" />
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          handleRemoveFile("cnic_back", setFieldValue)
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  {user.createLoading === true ? (
                    <Loading />
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={user.createLoading}
                    >
                      {"Create Employee"}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        }}
      </Formik>

      <Dialog
        open={showCredentialsDialog}
        onClose={handleCloseCredentialsDialog}
      >
        <DialogTitle>Employee Created Successfully!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please share these credentials with the new employee. You can copy
            the password below.
          </DialogContentText>
          <Box
            sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography>
              <strong>Email:</strong> {newEmployeeCredentials?.user?.email}
            </Typography>
            <Typography>
              <strong>Temporary Password:</strong>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={newEmployeeCredentials?.generatedPassword || ""}
              InputProps={{ readOnly: true }}
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCredentialsDialog}
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleCard>
  );
};

export default CreateEmployee;